import type React from 'react'
import type {ReactNode} from 'react'
import {createContext, useContext, useState, useCallback, useMemo} from 'react'
import type {Definition} from './DataDefinitionDrawer'
import DataDefinitionDrawer from './DataDefinitionDrawer'

// ─── Context value ───────────────────────────────────────────────────────────

interface DrawerConfig {
  title?: string
  definitions?: Definition[]
  content?: ReactNode
  scrollToId?: string
  width?: string
}

interface DataDefinitionDrawerContextValue {
  /** Open the right-side drawer with the given configuration */
  openDrawer: (config: DrawerConfig) => void
  /** Close the drawer */
  closeDrawer: () => void
}

const DataDefinitionDrawerContext = createContext<DataDefinitionDrawerContextValue | undefined>(undefined)

// ─── Provider ────────────────────────────────────────────────────────────────

export const DataDefinitionDrawerProvider: React.FC<{children: ReactNode}> = ({children}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [config, setConfig] = useState<DrawerConfig>({})

  const openDrawer = useCallback((cfg: DrawerConfig) => {
    setConfig(cfg)
    setIsOpen(true)
  }, [])

  const closeDrawer = useCallback(() => {
    setIsOpen(false)
  }, [])

  const value = useMemo(() => ({openDrawer, closeDrawer}), [openDrawer, closeDrawer])

  return (
    <DataDefinitionDrawerContext.Provider value={value}>
      {children}
      <DataDefinitionDrawer
        isOpen={isOpen}
        onClose={closeDrawer}
        title={config.title}
        definitions={config.definitions}
        scrollToId={config.scrollToId}
        width={config.width}
      >
        {config.content}
      </DataDefinitionDrawer>
    </DataDefinitionDrawerContext.Provider>
  )
}

// ─── Hook ────────────────────────────────────────────────────────────────────

export const useDataDefinitionDrawer = (): DataDefinitionDrawerContextValue => {
  const ctx = useContext(DataDefinitionDrawerContext)
  if (!ctx) {
    // Safe no-op fallback so components remain usable outside the provider (e.g. in tests)
    return {openDrawer: () => undefined, closeDrawer: () => undefined}
  }
  return ctx
}
